import axios from 'axios';
import * as Sentry from '@sentry/react';
import { useQueryClient } from 'react-query';
import toast from '../../../Components/Toast/Toast';

type UploadItemPhotosParams = {
  data: {
    url: string;
    file: File;
  }[];
  userId: string;
};

export const useUploadItemPhotosWithSignedUrl = () => {
  const queryClient = useQueryClient();

  const uploadItemPhotosWithSignedUrl = async ({
    data,
    userId,
  }: UploadItemPhotosParams) => {
    try {
      await Promise.all(
        data.map(async (value) => {
          const response = await axios({
            method: 'put',
            url: value.url,
            data: value.file,
            headers: {
              'Content-Type': value.file.type,
            },
          });
          if (response.status !== 200 && response.status !== 201) {
            throw new Error('Failed to upload item photos to AWS.');
          }
        }),
      );

      await queryClient.cancelQueries(['item-service-vendor-catalog', userId]);

      await queryClient.invalidateQueries([
        'item-service-count-remaining-item',
        userId,
      ]);
      await queryClient.invalidateQueries([
        'item-service-vendor-catalog-item',
        userId,
      ]);
      await queryClient.invalidateQueries([
        'item-service-vendor-catalog-items-count',
        userId,
      ]);
      await queryClient.invalidateQueries([
        'item-service-vendor-catalog',
        userId,
      ]);

      return {
        success: true,
      };
    } catch (e) {
      console.error('File upload error', e);

      // Capture detailed error info for Sentry
      const fileDetails = data.map((item, index) => ({
        index,
        fileName: item.file?.name,
        fileSize: item.file?.size,
        fileType: item.file?.type,
        urlPrefix: item.url?.substring(0, 50),
      }));

      Sentry.captureException(e, {
        tags: {
          feature: 'vendor-catalog-upload',
          type: 's3_upload_error',
        },
        extra: {
          userId,
          numberOfFiles: data.length,
          fileDetails,
          errorMessage: e instanceof Error ? e.message : String(e),
          // If it's an axios error, capture response details
          ...(axios.isAxiosError(e) && {
            responseStatus: e.response?.status,
            responseStatusText: e.response?.statusText,
            responseData: e.response?.data,
            requestUrl: e.config?.url?.substring(0, 100),
          }),
        },
      });

      toast('Failed to upload item photos', {
        type: 'error',
      });
      return {
        success: false,
        error: {
          message: 'Failed to upload item photos to AWS.',
        },
      };
    }
  };

  return { uploadItemPhotosWithSignedUrl };
};
